import React, { createContext, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useSelector, useDispatch } from 'react-redux';
import { refreshToken, logoutUser, updateUserProfile, loginWithFirebase } from '../store/slices/authSlice';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth as firebaseAuth, db as firebaseDb, isFirebaseConfigValid } from '../firebase/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { user, token, isAuthenticated, loading, userType } = useSelector(state => state.auth);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);
  // Toggle to control whether the frontend should call the backend.
  // Require an explicit opt-in (set to 'true') to enable backend calls.
  const useBackend = process.env.REACT_APP_USE_BACKEND === 'true';

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        if (useBackend) {
          try {
            // Verify token validity with backend refresh
            await dispatch(refreshToken()).unwrap();
          } catch (error) {
            // Token is invalid, logout
            dispatch(logoutUser());
            // If the failure is a network/backend outage or server error, treat it as transient
            const status = error?.status || error?.response?.status;
            const msg = error?.message || error?.response?.data?.message || String(error) || '';
            const isServerError = typeof status === 'number' && status >= 500;
            const isNetworkError = msg.includes('Network') || msg.includes('ECONNREFUSED') || msg.includes('connect') || !status;
            if (isServerError || isNetworkError) {
              toast.error('Backend unavailable — continuing in offline/fallback mode.');
            } else {
              setError(error);
            }
          }
        } else {
          // Firebase-only mode: restore local profile and token without calling backend
          try {
            const parsedUser = JSON.parse(storedUser);
            dispatch(updateUserProfile(parsedUser));
          } catch (err) {
            console.warn('Failed to restore local user from storage in Firebase-only mode:', err);
          }
        }
      }
      setIsInitialized(true);
    };

    initAuth();
  }, [dispatch]);

  // Auto logout on token expiry
  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiryTime = payload.exp * 1000;
        const currentTime = Date.now();
        
        if (expiryTime <= currentTime) {
          dispatch(logoutUser());
          return;
        }

        // Set timer to logout before token expires
        const timeUntilExpiry = expiryTime - currentTime - 60000; // 1 minute before
        const logoutTimer = setTimeout(() => {
          dispatch(logoutUser());
        }, timeUntilExpiry);

        return () => clearTimeout(logoutTimer);
      } catch (error) {
        console.error('Error parsing token:', error);
        dispatch(logoutUser());
      }
    }
  }, [token, dispatch]);

  const login = async (credentials) => {
    try {
      const userCred = await signInWithEmailAndPassword(firebaseAuth, credentials.email, credentials.password);
      const idToken = await userCred.user.getIdToken();
      // If backend integration is enabled, notify the backend to create/verify
      // the user (this may exchange the Firebase ID token for app tokens).
      if (process.env.REACT_APP_USE_BACKEND === 'true') {
        const result = await dispatch(loginWithFirebase({ idToken })).unwrap();
        setError(null);
        return { success: true, data: result, user: result.user, userType: result.user?.role };
      }

      // Firebase-only mode: persist the Firebase ID token locally and ensure
      // a professional user profile is available in Firestore (if enabled).
      const fbUser = userCred.user;
      const firestoreEnabled = process.env.REACT_APP_FIRESTORE_ENABLED === 'true' && firebaseDb;

      let userData = {
        uid: fbUser.uid,
        name: fbUser.displayName || '',
        email: fbUser.email || credentials.email,
        role: fbUser.role || fbUser.userType || 'user',
        phone: credentials.phone || null,
        partnerApproved: false,
        signupSource: 'firebase',
        profile: {
          displayName: fbUser.displayName || '',
          avatar: fbUser.photoURL || null,
        },
        metadata: {},
        createdAt: new Date().toISOString(),
      };

      if (firestoreEnabled) {
        try {
          const userDocRef = doc(firebaseDb, 'users', fbUser.uid);
          const userSnap = await getDoc(userDocRef);
          if (userSnap.exists()) {
            userData = { ...userData, ...userSnap.data() };
          } else {
            // Create a well-structured user document for future role checks
            await setDoc(userDocRef, userData, { merge: true });
          }
        } catch (fireErr) {
          console.warn('Failed to read/write user document in Firestore (non-fatal):', fireErr?.message || fireErr);
        }
      }

      // Persist locally and update redux
      localStorage.setItem('token', idToken);
      localStorage.setItem('user', JSON.stringify(userData));
      dispatch(updateUserProfile(userData));
      setError(null);
      return { success: true, user: userData, userType: userData.role, token: idToken };
    } catch (error) {
      setError(error);
      return { success: false, error };
    }
  };

  const logout = async () => {
    try {
      // Sign out from Firebase as well in case the user was authenticated via Firebase
      try {
        await firebaseSignOut(firebaseAuth);
      } catch (signOutErr) {
        // non-fatal: continue to clear app state
        console.debug('Firebase signOut failed (non-fatal):', signOutErr);
      }

      await dispatch(logoutUser()).unwrap();
      setError(null);
      return { success: true };
    } catch (error) {
      setError(error);
      return { success: false, error };
    }
  };

  // Keep Redux/localStorage in sync with Firebase auth state
  useEffect(() => {
    // Do not register Firebase listeners if config looks invalid (prevents noisy 400s)
    if (!isFirebaseConfigValid()) {
      console.warn('Firebase config invalid or missing API key — skipping Firebase auth listener.');
      return undefined;
    }
  // Require explicit opt-in for Firestore reads/writes in development to avoid
  // noisy RPC errors when Firestore rules block access. Set REACT_APP_FIRESTORE_ENABLED=true
  // to enable Firestore operations.
  const firestoreEnabled = process.env.REACT_APP_FIRESTORE_ENABLED === 'true';

    const unsubscribe = onAuthStateChanged(firebaseAuth, async (fbUser) => {
      if (fbUser) {
        try {
          const idToken = await fbUser.getIdToken();
          // If Firestore is disabled (or intentionally blocked by rules), skip
          // attempting to read user profile documents — fall back to a minimal
          // profile populated from the Firebase user object.
          if (!firestoreEnabled || !firebaseDb) {
            const minimal = { uid: fbUser.uid, name: fbUser.displayName || '', email: fbUser.email, role: fbUser.role || fbUser.userType || 'user', partnerApproved: false };
            localStorage.setItem('token', idToken);
            localStorage.setItem('user', JSON.stringify(minimal));
            dispatch(updateUserProfile(minimal));
            setError(null);
            return;
          }

          const userDocRef = doc(firebaseDb, 'users', fbUser.uid);
          let userSnap;
          try {
            userSnap = await getDoc(userDocRef);
          } catch (fireErr) {
            // Permission-denied or other Firestore errors shouldn't prevent the app
            // from continuing — fall back to a minimal profile and proceed.
            console.warn('Firestore getDoc failed while syncing auth state — falling back to minimal profile:', fireErr?.code || fireErr?.message || fireErr);
            const minimal = { uid: fbUser.uid, name: fbUser.displayName || '', email: fbUser.email, role: 'user', partnerApproved: false };
            // Persist locally like backend login would
            localStorage.setItem('token', idToken);
            localStorage.setItem('user', JSON.stringify(minimal));
            dispatch(updateUserProfile(minimal));
            setError(null);
            return;
          }

          const userData = userSnap.exists()
            ? { ...userSnap.data(), role: userSnap.data().role || userSnap.data().userType || 'user' }
            : { uid: fbUser.uid, name: fbUser.displayName || '', email: fbUser.email, role: 'user', partnerApproved: false };

          console.log('AuthContext: User data after Firestore fetch/fallback:', userData);

          // Persist locally like backend login would
          localStorage.setItem('token', idToken);
          localStorage.setItem('user', JSON.stringify(userData));

          // update redux user slice so other parts of the app see the user
          dispatch(updateUserProfile(userData));
          setError(null);
        } catch (err) {
          console.error('Error syncing firebase auth state:', err);
          setError(err);
        }
      } else {
        // If there's no firebase user, only logout if there's no backend token stored.
        const storedToken = localStorage.getItem('token');
        if (!storedToken) {
          dispatch(logoutUser());
        }
      }
    });

    return () => unsubscribe();
  }, [dispatch]);

  const hasRole = (role) => {
    if (!user) return false;
    if (Array.isArray(role)) {
      return role.includes(user.role);
    }
    return user.role === role;
  };

  const hasPermission = (permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  };

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    userType,
    isInitialized,
    login,
    logout,
    hasRole,
    hasPermission,
    error,
  };

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500">Error: {error.message}</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};